import { addDays, dayLabel, requestWindowLabel } from '@kronos/domain'
import type { LocalDate, ScheduleGrid } from '@kronos/domain'
import type { ComeInRequestView, TechnicianView } from '../../data/schedule'
import { Badge } from '../primitives'
import type { Ask } from './RequestForm'
import { STATE_LABEL, STATE_TONE, availabilityText, relativeDay } from './shared'

/**
 * The phone view of the same grid: one block per day, a row for each
 * technician who has said or been asked anything that day, and the people who
 * have not as one quiet line of buttons. Any row is a tap to ask them in.
 */
export function TeamDayList({
  grid,
  today,
  timeZone,
  onPick,
}: {
  grid: ScheduleGrid<ComeInRequestView, TechnicianView>
  today: LocalDate
  timeZone: string
  onPick: (ask: Ask) => void
}): JSX.Element {
  const tomorrow = addDays(today, 1)
  return (
    <div className="sched-days">
      {grid.dates.map((date, i) => {
        const entries = grid.rows.map((row) => ({ technician: row.technician, cell: row.cells[i] }))
        const said = entries.filter((e) => e.cell && (e.cell.availability || e.cell.requests.length > 0))
        const silent = entries.filter((e) => !said.includes(e))
        const past = date < today
        const when = relativeDay(date, today, tomorrow)
        return (
          <section key={date} className="sched-day">
            <h3 className="sched-day__head">
              {dayLabel(date)}
              {when ? <span className="muted"> · {when}</span> : null}
            </h3>
            <div className="panel">
              {said.map(({ technician, cell }) => (
                <button
                  key={technician.id}
                  className="row-item"
                  disabled={past}
                  onClick={() => onPick({ technicianId: technician.id, date })}
                >
                  <span className="row-item__main">
                    <span className="row-item__title">{technician.name}</span>
                    <span className="row-item__meta">
                      {cell?.availability ? availabilityText(cell.availability) : 'Not set'}
                      {cell?.availability?.note ? ` · ${cell.availability.note}` : ''}
                    </span>
                  </span>
                  <span className="row-item__side sched-day__reqs">
                    {cell?.requests.map((r) => (
                      <Badge key={r.id} tone={STATE_TONE[r.state]}>
                        {STATE_LABEL[r.state]} {requestWindowLabel(r, timeZone)}
                      </Badge>
                    ))}
                  </span>
                </button>
              ))}
              {silent.length > 0 ? (
                <div className="sched-day__silent">
                  <span className="muted">{said.length > 0 ? 'Not set:' : 'Nobody has said yet:'}</span>
                  {silent.map(({ technician }) => (
                    <button
                      key={technician.id}
                      className="section-button"
                      disabled={past}
                      onClick={() => onPick({ technicianId: technician.id, date })}
                    >
                      {technician.name}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </section>
        )
      })}
    </div>
  )
}
